import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Facebook, Instagram, Linkedin, ArrowRight } from "lucide-react";
import { useSite } from "@/lib/site";
import { useState } from "react";
import { subscribeNewsletter } from "@/lib/api";
import { toast } from "sonner";

export default function Footer() {
  const { site, cities } = useSite();
  const c = site.contact || {};
  const s = site.socials || {};
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      toast.success("Subscribed! We'll keep you posted.");
      setEmail("");
    } catch (err) {
      toast.error("Please enter a valid email.");
    } finally { setLoading(false); }
  };

  return (
    <footer className="bg-[color:var(--dh-navy)] text-white mt-20" data-testid="site-footer">
      <div className="container-x py-16 grid gap-10 sm:grid-cols-2 md:grid-cols-5">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3">
            <img src="/logo-256.png" alt="Digi Hub Solution" className="w-14 h-14 rounded-full object-cover ring-2 ring-white/20 shadow-lg" />
            <div className="leading-tight">
              <div className="font-display font-bold text-white text-lg">Digi Hub</div>
              <div className="text-[11px] tracking-[0.24em] font-semibold text-[color:var(--dh-orange-2)]">SOLUTION</div>
            </div>
          </div>
          <p className="mt-5 text-white/70 max-w-md leading-relaxed">
            Best-in-class consultation across IT, Business, Digital Media and Security. We combine creativity with technology to deliver solutions that drive measurable growth.
          </p>

          <form onSubmit={submit} className="mt-6 flex max-w-md" data-testid="newsletter-form">
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              data-testid="newsletter-input"
              className="flex-1 px-4 py-3 rounded-l-full bg-white/10 border border-white/15 text-white placeholder:text-white/50 focus:outline-none focus:border-[color:var(--dh-orange)]"
            />
            <button disabled={loading} className="px-5 rounded-r-full bg-[color:var(--dh-orange)] font-semibold hover:bg-[color:var(--dh-orange-2)] transition-colors flex items-center gap-2" data-testid="newsletter-submit">
              {loading ? "..." : <>Subscribe <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
        </div>

        <div>
          <div className="text-sm font-bold tracking-[0.2em] text-[color:var(--dh-orange-2)]">EXPLORE</div>
          <ul className="mt-4 space-y-2 text-white/80">
            {[
              ["Home", "/"], ["About", "/about"], ["Services", "/services"], ["Blog", "/blog"], ["Contact", "/contact"],
            ].map(([l, to]) => (
              <li key={to}><Link to={to} className="hover:text-[color:var(--dh-orange-2)] transition-colors" data-testid={`footer-link-${l.toLowerCase()}`}>{l}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <div className="text-sm font-bold tracking-[0.2em] text-[color:var(--dh-orange-2)]">WE SERVE IN</div>
          <ul className="mt-4 space-y-2 text-white/80">
            {cities.map((city) => (
              <li key={city.slug}><Link to={`/services/digital-marketing/${city.slug}`} className="hover:text-[color:var(--dh-orange-2)] transition-colors" data-testid={`footer-city-${city.slug}`}>{city.name}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <div className="text-sm font-bold tracking-[0.2em] text-[color:var(--dh-orange-2)]">GET IN TOUCH</div>
          <ul className="mt-4 space-y-3 text-white/80 text-sm">
            <li className="flex gap-3"><Phone className="w-4 h-4 mt-1 text-[color:var(--dh-orange-2)] shrink-0" /><a href="tel:+919808724448" data-testid="footer-phone">{c.phone1}</a></li>
            <li className="flex gap-3"><Mail className="w-4 h-4 mt-1 text-[color:var(--dh-orange-2)] shrink-0" /><a href={`mailto:${c.email}`} data-testid="footer-email" className="break-all">{c.email}</a></li>
            <li className="flex gap-3"><MapPin className="w-4 h-4 mt-1 text-[color:var(--dh-orange-2)] shrink-0" /><span data-testid="footer-address">{c.address}</span></li>
          </ul>
          <div className="mt-5 flex gap-3">
            {[
              { Icon: Facebook, href: s.facebook, label: "facebook" },
              { Icon: Instagram, href: s.instagram, label: "instagram" },
              { Icon: Linkedin, href: s.linkedin, label: "linkedin" },
            ].map(({ Icon, href, label }) => (
              <a
                href={href}
                key={label}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Visit our ${label} page`}
                className="w-9 h-9 grid place-items-center rounded-full bg-white/10 hover:bg-[color:var(--dh-orange)] transition-colors"
                data-testid={`social-${label}`}
              >
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-x py-5 flex flex-col md:flex-row items-center justify-between text-sm text-white/60 gap-2">
          <div>© {new Date().getFullYear()} Digi Hub Solution. All rights reserved.</div>
          <div className="flex gap-5">
            <Link to="/admin" className="hover:text-white" data-testid="admin-link">Admin</Link>
            <span>Made with care in Rishikesh, Uttarakhand</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
